import { Box, Flex, Spacer, Text, VStack } from '@chakra-ui/react';
import {
  Button,
  Divider,
  DividerLook,
  IconButton,
  Icons,
  SelectOption,
  SvgSpriteConsumer,
} from '@pluxee-design-system/core';
import { InputControl } from '@pluxee-design-system/react-hook-form';
import { CheckboxGroupControl, SelectControl } from 'common/forms';
import { ProductCheckboxItem } from 'components/Products/ProductCheckbox';
import { PosTypeEnum } from 'generated/models';
import { I18nProps, withTranslations } from 'i18n';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TerminalConfig } from 'types/config';
import { OnboardingValues } from '../types';
import { getPosTypeOption } from '../utils';
import ProductGroupControl from './ProductGroupControl';
import ProductWithLogo from './ProductWithLogo';

interface POSRowProps extends I18nProps {
  allowedPosTypes: PosTypeEnum[];
  config: TerminalConfig;
  children?: ReactNode;
  status?: ReactNode;
  transfer?: ReactNode;
  name: string;
  idPrefix?: string;
  index: number;
  onRemove?: (index: number) => void;
  isBoInfoVisible?: boolean;
  isMultiProductsAllowed?: boolean;
  isOnboarding?: boolean;
  isReadOnly?: boolean;
  isRemovable?: boolean;
  isDisabled?: boolean;
  isProductsDisabled?: boolean;
  isProductsExpanded?: boolean;
  showDivider?: boolean;

  products: ProductCheckboxItem[];
}

const POSRow = ({
  allowedPosTypes,
  config,
  children,
  idPrefix = '',
  index,
  isBoInfoVisible = false,
  isMultiProductsAllowed = false,
  isDisabled = false,
  isReadOnly = false,
  isProductsDisabled = false,
  isProductsExpanded = true,
  name,
  onRemove,
  products: contractProducts,
  showDivider = false,
  status,
  transfer,
  t,
}: POSRowProps) => {
  const [productsExpanded, expandProducts] = useState(isProductsExpanded);
  const toggleProducts = useCallback(() => expandProducts((prev) => !prev), [expandProducts]);
  const { setValue, watch } = useFormContext<OnboardingValues>();
  const valuesDeviceType = watch(`${name}deviceType` as 'POS.0.deviceType');
  const isDeviceTypeSupported = allowedPosTypes.includes(valuesDeviceType);
  const selectedProducts = watch(`${name}productIds` as 'POS.0.productIds');
  const isAppType = valuesDeviceType === PosTypeEnum.App;
  const productsCount = useMemo(
    () => Object.values(selectedProducts || {}).filter((x) => x).length,
    [selectedProducts],
  );
  const products = useMemo(
    () => contractProducts.filter((p) => p.allowedPosTypes.includes(valuesDeviceType)),
    [contractProducts, valuesDeviceType],
  );
  const devices = useMemo(
    () =>
      Object.values(PosTypeEnum)
        .map((val) => getPosTypeOption(val, t))
        .filter((d) => allowedPosTypes.includes(d.value as PosTypeEnum)),
    [allowedPosTypes, t],
  );
  const getUnsupportedDeviceType = useCallback((val: string) => getPosTypeOption(val, t), [t]);
  const handleRemove = useCallback(() => onRemove?.(index), [index, onRemove]);
  const handleDeviceTypeChange = useCallback(
    async (option: SelectOption) => {
      const selectedDeviceType = option.value as PosTypeEnum;
      const selectedProduct = contractProducts.find((p) => selectedProducts.includes(p.value));

      const shouldRemoveProducts = Boolean(
        selectedProduct && !selectedProduct.allowedPosTypes?.includes(selectedDeviceType),
      );

      if (shouldRemoveProducts)
        setValue(`${name}productIds` as 'POS.0.productIds', [], { shouldTouch: true });
      setValue(`${name}deviceType` as 'POS.0.deviceType', selectedDeviceType);
    },
    [contractProducts, name, selectedProducts, setValue],
  );

  useEffect(() => {
    if (!isDeviceTypeSupported)
      setValue(`${name}deviceType` as 'POS.0.deviceType', null as unknown as PosTypeEnum);
  }, [isDeviceTypeSupported, name, setValue]);

  useEffect(() => {
    const onlySupportedProducts = selectedProducts.filter((productId) =>
      contractProducts.some((product) => product.value === productId),
    );
    if (onlySupportedProducts.length !== selectedProducts.length)
      setValue(`${name}productIds` as 'POS.0.productIds', onlySupportedProducts, {
        shouldTouch: true,
      });
  }, [contractProducts, name, selectedProducts, setValue]);

  const removeButton = (
    <Button
      type="button"
      onClick={handleRemove}
      variant="secondaryTextOnly"
      id={`${idPrefix}${name}deleteButton`}
      size="md"
      alignSelf="flex-end"
      leftIcon={<SvgSpriteConsumer size={24} svgId={Icons.TRASH24} />}
    >
      {t('location_add.buttons.removeTerminal', 'Remove')}
    </Button>
  );

  const title = (
    <Text as="div" variant="subtitle.section">
      {index + 1}. {t('location_add.form.deviceTypeOptions.pos', 'Terminal')}
    </Text>
  );

  return (
    <VStack align="stretch" spacing={4} key={`POS-field-${index}`}>
      {status ? (
        <Flex>
          {title}
          <Spacer />
          {status}
        </Flex>
      ) : (
        title
      )}
      {transfer}
      {config.deviceType.visible && (
        <SelectControl
          disabled={isDisabled}
          readonly={isReadOnly}
          mandatory={config.deviceType.mandatory}
          name={`${name}deviceType`}
          id={`${name}deviceType`}
          // id={`${idPrefix}${name}deviceType`}
          placeholder={t('location_add.form.deviceType.placeholder', 'Device type')}
          title={t('location_add.form.deviceType.label', 'Device type')}
          notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
          options={devices}
          onChange={handleDeviceTypeChange}
          getNonOption={getUnsupportedDeviceType}
        />
      )}
      {config.terminalId.visible && (
        <InputControl
          disabled={isDisabled}
          readonly={isReadOnly}
          type="text"
          mandatory={config.terminalId.mandatory}
          name={`${name}terminalId`}
          id={`${idPrefix}${name}terminalId`}
          placeholder={
            isAppType
              ? t('location_add.form.applicationId.placeholder', 'Assigned application ID')
              : t('location_add.form.terminalId.placeholder', 'Terminal ID')
          }
          title={
            isAppType
              ? t('location_add.form.applicationId.label', 'Assigned application ID')
              : t('location_add.form.terminalId.label', 'Terminal ID')
          }
        />
      )}
      {config.email.visible && isAppType && (
        <InputControl
          disabled={isDisabled}
          readonly={isReadOnly}
          type="text"
          mandatory={config.email.mandatory || isAppType}
          name={`${name}email`}
          id={`${idPrefix}${name}email`}
          placeholder={t('location_add.form.email.placeholder', 'Email')}
          title={t('location_add.form.email.label', 'Email')}
        />
      )}
      {isBoInfoVisible && (
        <>
          <InputControl
            disabled
            readonly={isReadOnly}
            type="text"
            mandatory
            name={`${name}masterId`}
            id={`${idPrefix}${name}masterId`}
            placeholder={t('location_add.form.masterId.placeholder', 'Master ID')}
            title={t('location_add.form.masterId.label', 'Master ID')}
          />
          <InputControl
            disabled
            readonly={isReadOnly}
            type="text"
            mandatory
            name={`${name}terminalCode`}
            id={`${idPrefix}${name}terminalCode`}
            placeholder={t('location_add.form.terminalCode.placeholder', 'Terminal code')}
            title={t('location_add.form.terminalCode.label', 'Terminal code')}
          />
        </>
      )}
      {config.products.visible && (
        <VStack align="stretch" spacing={2} bgColor="semantic.surface.1" p={4}>
          <Flex gap={4} justify="space-between" width="100%" alignItems="center">
            <Text as="div" variant="subtitle.section">
              {t(
                'location_add.sectionTexts.addTerminal.numberOfAcceptedProducts',
                'Accepted products (%number%)',
                { number: productsCount },
              )}
            </Text>
            <Box>
              <IconButton
                aria-label="chevron"
                icon={
                  <SvgSpriteConsumer
                    size={24}
                    svgId={productsExpanded ? 'chevronUp24' : 'chevronDown24'}
                  />
                }
                onClick={toggleProducts}
                size="md"
                variant="icon"
              />
            </Box>
          </Flex>
          {productsExpanded && (
            <>
              {products.length === 0 && (
                <Text color="semantic.text.tertiary">
                  {t(
                    'location_add.sectionTexts.addTerminal.noProductsAvailable',
                    'There are no products to be added at the moment. Please add card product to contract and/or location first.',
                  )}
                </Text>
              )}
              {isMultiProductsAllowed ? (
                <CheckboxGroupControl
                  disabled={isProductsDisabled}
                  mandatory
                  name={`${name}productIds`}
                  id={`${idPrefix}${name}products`}
                  options={products}
                  renderLeftElement={ProductWithLogo}
                />
              ) : (
                <ProductGroupControl
                  disabled={isProductsDisabled}
                  mandatory
                  name={`${name}productIds`}
                  id={`${idPrefix}${name}products`}
                  options={products}
                  renderLeftElement={ProductWithLogo}
                />
              )}
            </>
          )}
        </VStack>
      )}
      {children ? (
        <Flex justify="space-between">
          {children}
          {onRemove && removeButton}
        </Flex>
      ) : (
        onRemove && removeButton
      )}
      {showDivider && <Divider look={DividerLook.SECONDARY} variant="solid" />}
    </VStack>
  );
};

export default withTranslations(POSRow);
